import { api } from '@/lib/api'
import type {
  CargoCreatePayload,
  CargoDetalhe,
  CargoResumo,
  ColaboradorDetalhe,
  ColaboradorResumo,
  CompetenciaTag,
  DepartamentoOpt,
  DnaItem,
  OrganogramaNo,
  Paginated,
  RhKpis,
  TimelineEvent,
} from './types'

export async function fetchRhKpis() {
  const res = await api.get<{ sucesso?: boolean; dados: RhKpis }>('/rh/colaboradores/kpis/')
  return res.dados
}

export async function fetchColaboradores(params: {
  busca?: string
  status?: string
  /** visivel (padrão) | sem | ocultas | inativas | excluidas | fora | todas */
  empresaFiltro?: string
  page?: number
  por_pagina?: number
  ordenar?: string
  onboarding?: string
  admitidos_dias?: number
} = {}) {
  const qs = new URLSearchParams()
  if (params.busca) qs.set('busca', params.busca)
  if (params.status) qs.set('status', params.status)
  if (params.empresaFiltro) qs.set('empresa_filtro', params.empresaFiltro)
  if (params.page) qs.set('page', String(params.page))
  if (params.por_pagina) qs.set('por_pagina', String(params.por_pagina))
  if (params.ordenar) qs.set('ordenar', params.ordenar)
  if (params.onboarding) qs.set('onboarding', params.onboarding)
  if (params.admitidos_dias != null) qs.set('admitidos_dias', String(params.admitidos_dias))
  const suffix = qs.toString() ? `?${qs}` : ''
  return api.get<Paginated<ColaboradorResumo>>(`/rh/colaboradores/${suffix}`)
}

export async function fetchColaborador(id: string) {
  const res = await api.get<{ sucesso?: boolean; dados: ColaboradorDetalhe }>(
    `/rh/colaboradores/${id}/`,
  )
  return res.dados
}

export type ColaboradorWritePayload = {
  nome: string
  cpf: string
  salario_base: number | string
  data_admissao: string
  email_corporativo?: string
  email_profissional?: string
  email?: string
  matricula?: string
  regime?: string
  status?: string
  cargo_id?: string | null
  departamento_id?: string | null
  gestor_id?: string | null
  rg?: string
  data_nascimento?: string | null
  sexo?: string
  estado_civil?: string
  nacionalidade?: string
  naturalidade?: string
  telefone?: string
  celular?: string
  cep?: string
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  estado?: string
  banco?: string
  agencia?: string
  conta?: string
  tipo_conta?: string
  pix_tipo?: string
  pix_chave?: string
  pis_pasep?: string
  ctps_numero?: string
  ctps_serie?: string
  ctps_uf?: string
  carga_horaria?: number | null
  observacoes?: string
  [key: string]: unknown
}

export async function createColaborador(payload: ColaboradorWritePayload) {
  const res = await api.post<{ sucesso?: boolean; dados: ColaboradorDetalhe }>(
    '/rh/colaboradores/',
    payload,
  )
  return res.dados
}

export async function updateColaborador(id: string, payload: Partial<ColaboradorWritePayload>) {
  const res = await api.patch<{ sucesso?: boolean; dados: ColaboradorDetalhe }>(
    `/rh/colaboradores/${id}/`,
    payload,
  )
  return res.dados
}

export async function deleteColaborador(id: string) {
  return api.delete<{ sucesso?: boolean }>(`/rh/colaboradores/${id}/`)
}

export type ImportarDetalhe = {
  nome?: string
  status?: string
  match?: string
  campos?: string[]
  cpf?: string
  cargo_status?: string
  codigo_cargo?: string
  mensagem_cargo?: string
}

export type ImportarColaboradoresResult = {
  sucesso?: boolean
  fase?: string
  tipo?: string
  arquivo?: string
  job_id?: string
  lidos?: number
  total?: number
  offset?: number
  limit?: number
  proximo_offset?: number
  concluido?: boolean
  lote_tamanho?: number
  atualizados?: number
  criados?: number
  nao_casados?: number
  conflitos_cpf?: number
  desligados?: number
  cargos_vinculados?: number
  cargos_pendentes?: number
  codigos_cargo_pendentes?: string[]
  dry_run?: boolean
  detalhes?: ImportarDetalhe[]
  mensagem?: string
}

export type ImportarProgresso = {
  done: number
  total: number
  atualizados: number
  criados: number
  nao_casados: number
  conflitos_cpf: number
  desligados: number
  cargos_vinculados: number
  cargos_pendentes: number
  loteAtual: number
  lotes: number
}

const IMPORT_LOTE = 40
const IMPORT_TIMEOUT_MS = 60_000

export async function importarColaboradoresIniciar(file: File) {
  const fd = new FormData()
  fd.append('fase', 'iniciar')
  fd.append('arquivo', file)
  return api.upload<ImportarColaboradoresResult>('/rh/colaboradores/importar/', fd, {
    timeoutMs: IMPORT_TIMEOUT_MS,
  })
}

export async function importarColaboradoresLote(
  jobId: string,
  opts: { offset: number; limit?: number; dryRun?: boolean; criarFaltantes?: boolean },
) {
  return api.post<ImportarColaboradoresResult>(
    '/rh/colaboradores/importar/',
    {
      fase: 'lote',
      job_id: jobId,
      offset: opts.offset,
      limit: opts.limit ?? IMPORT_LOTE,
      dry_run: opts.dryRun ? '1' : '0',
      criar_faltantes: opts.criarFaltantes ? '1' : '0',
    },
    { timeoutMs: IMPORT_TIMEOUT_MS },
  )
}

/** Sobe o arquivo e processa em lotes, reportando progresso. */
export async function importarColaboradoresArquivo(
  file: File,
  opts: {
    dryRun?: boolean
    criarFaltantes?: boolean
    onProgress?: (p: ImportarProgresso) => void
  } = {},
) {
  const init = await importarColaboradoresIniciar(file)
  const jobId = init.job_id
  const total = init.total ?? 0
  if (!jobId) throw new Error('Job de importação sem id.')
  if (total === 0) {
    return {
      ...init,
      lidos: 0,
      atualizados: 0,
      criados: 0,
      nao_casados: 0,
      conflitos_cpf: 0,
      detalhes: [],
      concluido: true,
    } satisfies ImportarColaboradoresResult
  }

  const loteSize = init.lote_tamanho || IMPORT_LOTE
  const lotes = Math.max(1, Math.ceil(total / loteSize))
  let offset = 0
  let atualizados = 0
  let criados = 0
  let naoCasados = 0
  let conflitos = 0
  let desligados = 0
  let cargosVinculados = 0
  let cargosPendentes = 0
  const codigosPendentes = new Set<string>()
  const detalhes: ImportarDetalhe[] = []
  let dryRun = Boolean(opts.dryRun)
  let tipo = init.tipo
  let arquivo = init.arquivo

  while (offset < total) {
    const lote = await importarColaboradoresLote(jobId, {
      offset,
      limit: loteSize,
      dryRun: opts.dryRun,
      criarFaltantes: opts.criarFaltantes,
    })
    atualizados += lote.atualizados ?? 0
    criados += lote.criados ?? 0
    naoCasados += lote.nao_casados ?? 0
    conflitos += lote.conflitos_cpf ?? 0
    desligados += lote.desligados ?? 0
    cargosVinculados += lote.cargos_vinculados ?? 0
    cargosPendentes += lote.cargos_pendentes ?? 0
    for (const cod of lote.codigos_cargo_pendentes || []) codigosPendentes.add(cod)
    if (lote.detalhes?.length) detalhes.push(...lote.detalhes)
    dryRun = Boolean(lote.dry_run ?? opts.dryRun)
    tipo = lote.tipo || tipo
    arquivo = lote.arquivo || arquivo
    const next = lote.proximo_offset ?? offset + loteSize
    const done = Math.min(next, total)
    const loteAtual = Math.min(lotes, Math.floor(done / loteSize) || 1)
    opts.onProgress?.({
      done,
      total,
      atualizados,
      criados,
      nao_casados: naoCasados,
      conflitos_cpf: conflitos,
      desligados,
      cargos_vinculados: cargosVinculados,
      cargos_pendentes: cargosPendentes,
      loteAtual,
      lotes,
    })
    if (lote.concluido || next <= offset) break
    offset = next
  }

  return {
    sucesso: true,
    fase: 'lotes',
    tipo,
    arquivo,
    job_id: jobId,
    lidos: total,
    total,
    atualizados,
    criados,
    nao_casados: naoCasados,
    conflitos_cpf: conflitos,
    desligados,
    cargos_vinculados: cargosVinculados,
    cargos_pendentes: cargosPendentes,
    codigos_cargo_pendentes: [...codigosPendentes].sort().slice(0, 80),
    dry_run: dryRun,
    concluido: true,
    detalhes: detalhes.slice(0, 120),
  } satisfies ImportarColaboradoresResult
}

export async function fetchColaboradorTimeline(id: string) {
  const res = await api.get<{ sucesso?: boolean; dados: TimelineEvent[] }>(
    `/rh/colaboradores/${id}/timeline/`,
  )
  return res.dados || []
}

export async function fetchAniversariantes(periodo: string = 'mes') {
  const res = await api.get<{
    sucesso?: boolean
    dados: ColaboradorResumo[]
    total?: number
    periodo?: string
  }>(`/rh/aniversariantes/?periodo=${encodeURIComponent(periodo)}`)
  return res
}

export async function fetchCargos(params: {
  busca?: string
  area?: string
  status?: string
  page?: number
  completo?: boolean
  /** Só ativos com headcount > 0 */
  comColaboradores?: boolean
} = {}) {
  const qs = new URLSearchParams()
  if (params.completo) qs.set('completo', '1')
  if (params.busca) qs.set('busca', params.busca)
  if (params.area) qs.set('area', params.area)
  if (params.status) qs.set('status', params.status)
  if (params.page) qs.set('page', String(params.page))
  if (params.comColaboradores) qs.set('com_colaboradores', '1')
  const suffix = qs.toString() ? `?${qs}` : ''
  return api.get<Paginated<CargoResumo>>(`/rh/cargos/${suffix}`)
}

export async function createCargo(payload: CargoCreatePayload) {
  const res = await api.post<{ sucesso?: boolean; dados: CargoDetalhe }>('/rh/cargos/', payload)
  return res.dados
}

export async function fetchCargo(id: string) {
  const res = await api.get<{ sucesso?: boolean; dados: CargoDetalhe }>(`/rh/cargos/${id}/`)
  return res.dados
}

/** Snapshot para Nova Vaga (textos + faixa; não altera o cargo). */
export type CargoPrefillVaga = {
  cargo_id: string
  titulo: string
  departamento_id?: string | null
  departamento_nome?: string
  descricao: string
  requisitos: string
  beneficios: string
  salario_min?: number | null
  salario_max?: number | null
  nivel?: string
  codigo_cbo?: string
  area?: string
  tem_descritivo?: boolean
  descritivo_de_cargo_id?: string | null
  descritivo_de_cargo_nome?: string | null
}

export async function fetchCargoPrefillVaga(id: string) {
  const res = await api.get<{ sucesso?: boolean; dados: CargoPrefillVaga }>(
    `/rh/cargos/${id}/prefill-vaga/`,
  )
  return res.dados
}

export async function deleteCargo(id: string) {
  return api.delete<{ sucesso?: boolean; mensagem?: string }>(`/rh/cargos/${id}/`)
}

export type ImportarCargosResult = {
  sucesso?: boolean
  fase?: string
  tipo?: string
  arquivo?: string
  job_id?: string
  lidos?: number
  total?: number
  offset?: number
  proximo_offset?: number
  concluido?: boolean
  lote_tamanho?: number
  atualizados?: number
  criados?: number
  nao_casados?: number
  inativos?: number
  dry_run?: boolean
  detalhes?: Array<{
    nome?: string
    status?: string
    match?: string
    codigo?: string
    campos?: string[]
    ativo?: boolean
  }>
  mensagem?: string
}

export type ImportarCargosProgresso = {
  done: number
  total: number
  atualizados: number
  criados: number
  nao_casados: number
  inativos: number
  loteAtual: number
  lotes: number
}

const CARGOS_LOTE = 50
const CARGOS_TIMEOUT_MS = 60_000

export async function importarCargosIniciar(file: File) {
  const fd = new FormData()
  fd.append('fase', 'iniciar')
  fd.append('arquivo', file)
  return api.upload<ImportarCargosResult>('/rh/cargos/importar/', fd, {
    timeoutMs: CARGOS_TIMEOUT_MS,
  })
}

export async function importarCargosLote(
  jobId: string,
  opts: { offset: number; limit?: number; dryRun?: boolean; criarFaltantes?: boolean },
) {
  return api.post<ImportarCargosResult>(
    '/rh/cargos/importar/',
    {
      fase: 'lote',
      job_id: jobId,
      offset: opts.offset,
      limit: opts.limit ?? CARGOS_LOTE,
      dry_run: opts.dryRun ? '1' : '0',
      criar_faltantes: opts.criarFaltantes ? '1' : '0',
    },
    { timeoutMs: CARGOS_TIMEOUT_MS },
  )
}

/** Descritivo de cargos (CSV/XLSX) — atualiza objetivo, responsabilidades, tags, etc. */
export async function importarDescritivoCargos(file: File) {
  const fd = new FormData()
  fd.append('arquivo', file)
  return api.upload<ImportarCargosResult & {
    lidos?: number
    criados?: number
    atualizados?: number
    duplicados_removidos?: number
    ativos?: number
  }>('/rh/cargos/importar/', fd, {
    timeoutMs: 120_000,
  })
}

/** Dicionário de Competências (XLSX) → banco de tags. */
export async function importarDicionarioCompetencias(file: File) {
  const fd = new FormData()
  fd.append('arquivo', file)
  return api.upload<{
    sucesso?: boolean
    lidos?: number
    criados?: number
    atualizados?: number
    ativos?: number
    tipo?: string
    mensagem?: string
  }>('/rh/competencias/importar/', fd, {
    timeoutMs: 120_000,
  })
}

export async function importarCargosArquivo(
  file: File,
  opts: {
    dryRun?: boolean
    criarFaltantes?: boolean
    onProgress?: (p: ImportarCargosProgresso) => void
  } = {},
) {
  const init = await importarCargosIniciar(file)
  const jobId = init.job_id
  const total = init.total ?? 0
  if (!jobId) throw new Error('Job de importação de cargos sem id.')
  if (total === 0) {
    return {
      ...init,
      lidos: 0,
      atualizados: 0,
      criados: 0,
      nao_casados: 0,
      inativos: 0,
      detalhes: [],
      concluido: true,
    } satisfies ImportarCargosResult
  }

  const loteSize = init.lote_tamanho || CARGOS_LOTE
  const lotes = Math.max(1, Math.ceil(total / loteSize))
  let offset = 0
  let atualizados = 0
  let criados = 0
  let naoCasados = 0
  let inativos = 0
  const detalhes: NonNullable<ImportarCargosResult['detalhes']> = []
  let dryRun = Boolean(opts.dryRun)
  let tipo = init.tipo
  let arquivo = init.arquivo

  while (offset < total) {
    const lote = await importarCargosLote(jobId, {
      offset,
      limit: loteSize,
      dryRun: opts.dryRun,
      criarFaltantes: opts.criarFaltantes,
    })
    atualizados += lote.atualizados ?? 0
    criados += lote.criados ?? 0
    naoCasados += lote.nao_casados ?? 0
    inativos += lote.inativos ?? 0
    if (lote.detalhes?.length) detalhes.push(...lote.detalhes)
    dryRun = Boolean(lote.dry_run ?? opts.dryRun)
    tipo = lote.tipo || tipo
    arquivo = lote.arquivo || arquivo
    const next = lote.proximo_offset ?? offset + loteSize
    const done = Math.min(next, total)
    const loteAtual = Math.min(lotes, Math.floor(done / loteSize) || 1)
    opts.onProgress?.({
      done,
      total,
      atualizados,
      criados,
      nao_casados: naoCasados,
      inativos,
      loteAtual,
      lotes,
    })
    if (lote.concluido || next <= offset) break
    offset = next
  }

  return {
    sucesso: true,
    fase: 'lotes',
    tipo,
    arquivo,
    job_id: jobId,
    lidos: total,
    total,
    atualizados,
    criados,
    nao_casados: naoCasados,
    inativos,
    dry_run: dryRun,
    concluido: true,
    detalhes: detalhes.slice(0, 120),
  } satisfies ImportarCargosResult
}

export async function updateCargo(id: string, payload: Partial<CargoCreatePayload>) {
  const res = await api.patch<{ sucesso?: boolean; dados: CargoDetalhe }>(
    `/rh/cargos/${id}/`,
    payload,
  )
  return res.dados
}

export async function fetchOrganograma() {
  const res = await api.get<{
    sucesso?: boolean
    dados: OrganogramaNo
  }>('/rh/departamentos/organograma/')
  return res.dados
}

export async function fetchDepartamentos() {
  const res = await api.get<{ sucesso?: boolean; dados: DepartamentoOpt[] }>(
    '/rh/departamentos/',
  )
  return res.dados || []
}

export async function fetchCompetencias(params: {
  tipo?: string
  busca?: string
  ativos?: boolean
  page?: number
  por_pagina?: number
} = {}) {
  const qs = new URLSearchParams()
  if (params.tipo) qs.set('tipo', params.tipo)
  if (params.busca) qs.set('busca', params.busca)
  if (params.ativos) qs.set('ativos', '1')
  if (params.page) qs.set('page', String(params.page))
  if (params.por_pagina) qs.set('por_pagina', String(params.por_pagina))
  const suffix = qs.toString() ? `?${qs}` : ''
  return api.get<Paginated<CompetenciaTag>>(`/rh/competencias/${suffix}`)
}

export async function createCompetencia(nome: string, tipo: string) {
  const res = await api.post<{ sucesso?: boolean; dados: CompetenciaTag }>('/rh/competencias/', {
    nome,
    tipo,
  })
  return res.dados
}

export async function patchCompetencia(id: string, data: { nome?: string; ativo?: boolean }) {
  const res = await api.patch<{ sucesso?: boolean; dados: CompetenciaTag }>(
    `/rh/competencias/${id}/`,
    data,
  )
  return res.dados
}

export async function deleteCompetencia(id: string) {
  return api.delete<{ sucesso?: boolean; mensagem?: string }>(`/rh/competencias/${id}/`)
}

export type EmpresaResumo = {
  id: string
  codigo_dominio?: string
  nome: string
  nome_fantasia?: string
  cnpj?: string
  cidade?: string
  estado?: string
  cnae?: string
  email?: string
  telefone?: string
  inscricao_estadual?: string
  inscricao_municipal?: string
  cep?: string
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  responsavel_legal?: string
  data_inicio?: string | null
  principal?: boolean
  ativo?: boolean
  oculta?: boolean
  editado_manual?: boolean
  headcount?: number
}

export type EmpresaWritePayload = {
  codigo_dominio?: string
  nome: string
  nome_fantasia?: string
  cnpj?: string
  inscricao_estadual?: string
  inscricao_municipal?: string
  cnae?: string
  email?: string
  telefone?: string
  cep?: string
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  estado?: string
  responsavel_legal?: string
  data_inicio?: string | null
  principal?: boolean
  ativo?: boolean
  oculta?: boolean
}

export type ImportarEmpresasResult = {
  sucesso?: boolean
  fase?: string
  tipo?: string
  arquivo?: string
  job_id?: string
  lidos?: number
  total?: number
  offset?: number
  proximo_offset?: number
  concluido?: boolean
  lote_tamanho?: number
  atualizados?: number
  criados?: number
  nao_casados?: number
  inativos?: number
  divergentes?: number
  atualizar_editadas?: boolean
  dry_run?: boolean
  detalhes?: Array<{
    nome?: string
    status?: string
    match?: string
    codigo?: string
    cnpj?: string
    campos?: string[]
    ativo?: boolean
    empresa_id?: string
    mensagem?: string
    divergencias?: Array<{ campo?: string; atual?: string; novo?: string }>
  }>
  vinculo_colaboradores?: { vinculados?: number; pendentes?: number }
  vinculo_cargos?: { vinculados?: number; pendentes?: number }
  mensagem?: string
}

export type ImportarEmpresasProgresso = {
  done: number
  total: number
  atualizados: number
  criados: number
  nao_casados: number
  inativos: number
  divergentes: number
  loteAtual: number
  lotes: number
}

const EMPRESAS_LOTE = 40
const EMPRESAS_TIMEOUT_MS = 60_000

export async function fetchEmpresas(params: {
  busca?: string
  status?: string
  page?: number
} = {}) {
  const qs = new URLSearchParams()
  if (params.busca) qs.set('busca', params.busca)
  if (params.status) qs.set('status', params.status)
  if (params.page) qs.set('page', String(params.page))
  const suffix = qs.toString() ? `?${qs}` : ''
  return api.get<Paginated<EmpresaResumo>>(`/rh/empresas/${suffix}`)
}

export async function fetchEmpresa(id: string) {
  const res = await api.get<{ sucesso?: boolean; dados: EmpresaResumo }>(`/rh/empresas/${id}/`)
  return res.dados
}

export async function createEmpresa(payload: EmpresaWritePayload) {
  const res = await api.post<{ sucesso?: boolean; dados: EmpresaResumo }>('/rh/empresas/', payload)
  return res.dados
}

export async function updateEmpresa(id: string, payload: Partial<EmpresaWritePayload>) {
  const res = await api.patch<{ sucesso?: boolean; dados: EmpresaResumo }>(
    `/rh/empresas/${id}/`,
    payload,
  )
  return res.dados
}

export async function deleteEmpresa(id: string) {
  return api.delete<{ sucesso?: boolean; mensagem?: string }>(`/rh/empresas/${id}/`)
}

export type EmpresaMassaAcao =
  | 'principal'
  | 'nao_principal'
  | 'ativar'
  | 'inativar'
  | 'ocultar'
  | 'mostrar'
  | 'excluir'

export async function bulkEmpresas(ids: string[], acao: EmpresaMassaAcao) {
  return api.post<{
    sucesso?: boolean
    afetados?: number
    solicitados?: number
    mensagem?: string
  }>('/rh/empresas/massa/', { ids, acao })
}

export async function importarEmpresasIniciar(file: File) {
  const fd = new FormData()
  fd.append('fase', 'iniciar')
  fd.append('arquivo', file)
  return api.upload<ImportarEmpresasResult>('/rh/empresas/importar/', fd, {
    timeoutMs: EMPRESAS_TIMEOUT_MS,
  })
}

export async function importarEmpresasLote(
  jobId: string,
  opts: {
    offset: number
    limit?: number
    dryRun?: boolean
    criarFaltantes?: boolean
    vincular?: boolean
    atualizarEditadas?: boolean
  },
) {
  return api.post<ImportarEmpresasResult>(
    '/rh/empresas/importar/',
    {
      fase: 'lote',
      job_id: jobId,
      offset: opts.offset,
      limit: opts.limit ?? EMPRESAS_LOTE,
      dry_run: opts.dryRun ? '1' : '0',
      criar_faltantes: opts.criarFaltantes ? '1' : '0',
      vincular: opts.vincular ? '1' : '0',
      atualizar_editadas: opts.atualizarEditadas ? '1' : '0',
    },
    { timeoutMs: EMPRESAS_TIMEOUT_MS },
  )
}

export async function importarEmpresasArquivo(
  file: File,
  opts: {
    dryRun?: boolean
    criarFaltantes?: boolean
    vincular?: boolean
    atualizarEditadas?: boolean
    onProgress?: (p: ImportarEmpresasProgresso) => void
  } = {},
) {
  const init = await importarEmpresasIniciar(file)
  const jobId = init.job_id
  const total = init.total ?? 0
  if (!jobId) throw new Error('Job de importação de empresas sem id.')
  if (total === 0) {
    return {
      ...init,
      lidos: 0,
      atualizados: 0,
      criados: 0,
      nao_casados: 0,
      inativos: 0,
      divergentes: 0,
      detalhes: [],
      concluido: true,
    } satisfies ImportarEmpresasResult
  }

  const loteSize = init.lote_tamanho || EMPRESAS_LOTE
  const lotes = Math.max(1, Math.ceil(total / loteSize))
  let offset = 0
  let atualizados = 0
  let criados = 0
  let naoCasados = 0
  let inativos = 0
  let divergentes = 0
  const detalhes: NonNullable<ImportarEmpresasResult['detalhes']> = []
  let dryRun = Boolean(opts.dryRun)
  let tipo = init.tipo
  let arquivo = init.arquivo
  let vinculoColab: ImportarEmpresasResult['vinculo_colaboradores']
  let vinculoCargos: ImportarEmpresasResult['vinculo_cargos']

  while (offset < total) {
    const isLast = offset + loteSize >= total
    const lote = await importarEmpresasLote(jobId, {
      offset,
      limit: loteSize,
      dryRun: opts.dryRun,
      criarFaltantes: opts.criarFaltantes,
      vincular: isLast ? opts.vincular : false,
      atualizarEditadas: opts.atualizarEditadas,
    })
    atualizados += lote.atualizados ?? 0
    criados += lote.criados ?? 0
    naoCasados += lote.nao_casados ?? 0
    inativos += lote.inativos ?? 0
    divergentes += lote.divergentes ?? 0
    if (lote.detalhes?.length) detalhes.push(...lote.detalhes)
    dryRun = Boolean(lote.dry_run ?? opts.dryRun)
    tipo = lote.tipo || tipo
    arquivo = lote.arquivo || arquivo
    if (lote.vinculo_colaboradores) vinculoColab = lote.vinculo_colaboradores
    if (lote.vinculo_cargos) vinculoCargos = lote.vinculo_cargos
    const next = lote.proximo_offset ?? offset + loteSize
    const done = Math.min(next, total)
    const loteAtual = Math.min(lotes, Math.floor(done / loteSize) || 1)
    opts.onProgress?.({
      done,
      total,
      atualizados,
      criados,
      nao_casados: naoCasados,
      inativos,
      divergentes,
      loteAtual,
      lotes,
    })
    if (lote.concluido || next <= offset) break
    offset = next
  }

  return {
    sucesso: true,
    fase: 'lotes',
    tipo,
    arquivo,
    job_id: jobId,
    lidos: total,
    total,
    atualizados,
    criados,
    nao_casados: naoCasados,
    inativos,
    divergentes,
    dry_run: dryRun,
    concluido: true,
    detalhes: detalhes.slice(0, 120),
    vinculo_colaboradores: vinculoColab,
    vinculo_cargos: vinculoCargos,
  } satisfies ImportarEmpresasResult
}

export type SyncRhidResult = {
  sucesso?: boolean
  mensagem?: string
  somente_cadastro?: boolean
  sincronizados?: number
  periodo?: [string, string]
  vinculo?: {
    pessoas_rhid?: number
    vinculados?: number
    cadastro_atualizado?: number
    ja_vinculados?: number
    sem_match?: number
    detalhes?: Array<{
      nome?: string
      id_rhid?: number
      match?: string
      campos?: string[]
    }>
  }
  erros?: Array<{ colaborador_id?: string; erro?: string }>
}

export type SyncBitrixResult = {
  sucesso?: boolean
  mensagem?: string
  origem?: string
  async?: boolean
  job_id?: string
  status?: 'queued' | 'running' | 'done' | 'error' | 'unknown' | string
  ativos_bitrix?: number
  inativos_bitrix?: number
  criados?: number
  atualizados?: number
  inalterados?: number
  sem_match?: number
  desligados?: number
  reativados?: number
  fotos?: number
  fotos_puladas_match_fraco?: number
  erros?: number
  criar_faltantes?: boolean
  somente_fotos?: boolean
  detalhes_erro?: Array<{ id?: string | number; erro?: string }>
}

const SYNC_TIMEOUT_MS = 180_000
const BITRIX_POLL_MS = 2_500
const BITRIX_POLL_MAX_MS = 45 * 60_000

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms))
}

export async function syncRhidCadastro(opts: {
  dias?: number
  somenteCadastro?: boolean
  atualizarCadastro?: boolean
} = {}) {
  return api.post<SyncRhidResult>(
    '/integracoes/rhid/sync/',
    {
      dias: opts.dias ?? 30,
      somente_cadastro: opts.somenteCadastro ? '1' : '0',
      atualizar_cadastro: opts.atualizarCadastro === false ? '0' : '1',
    },
    { timeoutMs: SYNC_TIMEOUT_MS },
  )
}

export async function fetchBitrixSyncStatus(jobId: string) {
  return api.get<SyncBitrixResult>(
    `/integracoes/bitrix/sync/status/?job_id=${encodeURIComponent(jobId)}`,
    { timeoutMs: 30_000, quiet: true },
  )
}

/** Inicia sync Bitrix (async no backend) e faz poll até concluir. */
export async function syncBitrixCadastro(opts: {
  forceFotos?: boolean
  criarFaltantes?: boolean
  /** Só rebaixa foto de quem já tem id_bitrix (corrige fotos trocadas). */
  somenteFotos?: boolean
  onProgress?: (status: SyncBitrixResult) => void
} = {}) {
  const start = await api.post<SyncBitrixResult>(
    '/integracoes/bitrix/sync/',
    {
      force_fotos: opts.forceFotos || opts.somenteFotos ? '1' : '0',
      criar_faltantes: opts.criarFaltantes ? '1' : '0',
      somente_fotos: opts.somenteFotos ? '1' : '0',
    },
    { timeoutMs: 60_000 },
  )

  // Legado síncrono (sync=1) ou resposta já final
  if (!start.async || !start.job_id) {
    return start
  }

  opts.onProgress?.(start)
  const deadline = Date.now() + BITRIX_POLL_MAX_MS
  while (Date.now() < deadline) {
    await sleep(BITRIX_POLL_MS)
    const st = await fetchBitrixSyncStatus(start.job_id)
    opts.onProgress?.(st)
    if (st.status === 'done' || st.status === 'error') {
      return st
    }
  }
  return {
    sucesso: false,
    status: 'error',
    job_id: start.job_id,
    mensagem: 'Tempo esgotado aguardando o sync Bitrix. Veja os logs do container web.',
  }
}

export type DuplicadoColabInfo = {
  id: string
  nome?: string
  cpf?: string
  cpf_sintetico?: boolean
  matricula?: string
  id_bitrix?: string
  id_rhid?: string
  email?: string
  departamento?: string
  tem_foto?: boolean
  status?: string
}

export type DuplicadoPar = {
  a: DuplicadoColabInfo
  b: DuplicadoColabInfo
  sugerido_manter_id: string
  sugerido_remover_id: string
  seguro?: boolean
}

export async function fetchDuplicadosColaboradores() {
  return api.get<{ sucesso?: boolean; total?: number; dados: DuplicadoPar[] }>(
    '/integracoes/colaboradores/duplicados/',
  )
}

export async function mesclarDuplicadosAuto(opts: { dryRun?: boolean } = {}) {
  return api.post<{
    sucesso?: boolean
    pares?: number
    mesclados?: number
    dry_run?: boolean
    detalhes?: unknown[]
    mensagem?: string
  }>(
    '/integracoes/colaboradores/mesclar/',
    { modo: 'auto', dry_run: opts.dryRun ? '1' : '0' },
    { timeoutMs: SYNC_TIMEOUT_MS },
  )
}

export async function mesclarDuplicadoPar(opts: {
  manterId: string
  removerId: string
  dryRun?: boolean
  forcar?: boolean
}) {
  return api.post<{
    sucesso?: boolean
    mensagem?: string
    mesclados?: number
    dry_run?: boolean
    detalhe?: unknown
  }>(
    '/integracoes/colaboradores/mesclar/',
    {
      modo: 'par',
      manter_id: opts.manterId,
      remover_id: opts.removerId,
      dry_run: opts.dryRun ? '1' : '0',
      forcar: opts.forcar ? '1' : '0',
    },
    { timeoutMs: SYNC_TIMEOUT_MS },
  )
}

export async function fetchDnaCorporativo() {
  const res = await api.get<{ sucesso?: boolean; dados: DnaItem[] }>('/inteligencia/dna/')
  return res.dados || []
}

export async function calcularDna(referencia?: string) {
  return api.post<{ sucesso?: boolean; dados: DnaItem }>('/inteligencia/dna/calcular/', {
    referencia,
  })
}
